import React, { useEffect, useRef, useState, useCallback } from "react";
import "./topic.scss";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";

import TableWithPagination from "../../components/Table/TableWithPagination";
import TopicInputModal from "./TopicInputModal";
import SimpleModal from "../../components/Table/SimpleModal";
import { useDispatch, useSelector } from "react-redux";
import {
  addResource,
  addTopic,
  clearResources,
  clearTopicData,
  cloneTopic,
  deleteResource,
  deleteTopic,
  editResource,
  editTopic,
  getResourceByTopicId,
  getTopic,
  lockTopic,
} from "../../store/topic";
import NoDataAvailable from "../../components/NoDataAvailable/NoDataAvailable";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddResourceModal from "../../components/AddResourceModal/AddResourceModal";
import ResourceTableModal from "../../components/ResourceTableModal/ResourceTableModal";
import { useNavigate } from "react-router-dom";
import Constants from "../../constants/constants";
import Loader from "../../components/Loader/Loader";
import { isValidUrlFormat } from "../../utils/utils";

const initialTopicData = {
  id: "",
  name: "",
  description: "",
  agenda: "",
  offset: 0,
  filterBy: "",
};

const initialResourceDetails = {
  json: {
    id: "",
    name: "",
    format: "URL",
    type: Constants.TOPIC,
    url: null,
  },
  files: null,
  offset: 0,
  filterBy: "",
};

const Topic = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const topic = useSelector((state) => {
    return state.topic;
  });
  const isResourceLoading = topic?.resourcesLoading;
  const getTopicData = topic?.topicData;
  const loading = topic?.loading;
  const [isEmptyMsg, setIsEmptyMsg] = useState("");
  const [offset, setOffset] = useState(0);
  const [offsetChangedByUser, setOffsetChangedByUser] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const handlePageChange = (value) => {
    setOffsetChangedByUser(true);
    setCurrentPage(value);
    setOffset(value - 1);
  };
  const totalResult = getTopicData?.data?.totalResults;

  const [topicData, setTopicData] = useState(initialTopicData);

  const [filterBy, setFilterBy] = useState("");
  const inputRef = useRef(null);
  const [timeoutId, setTimeoutId] = useState(null);
  const [resourceId, setResourceId] = useState(0);

  const resourceOptions = useSelector(
    (state) => state?.utility?.constants?.resourceFormats
  );

  //////////////////////////////////////// Permission handling ////////////////////////////////////////

  const { permission } = useSelector((state) => state?.security);
  const isSpoc = permission?.spoc;

  const accessToCreate = isSpoc;
  const accessToAddResources = isSpoc;
  const accessToActions = isSpoc;
  const accessToResourcesTableAction = isSpoc;

  /////////////////////////////////////////////////////////////////////////////////////////////////////

  const handleFilterChange = (value) => {
    setFilterBy(value);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    const newTimeoutId = setTimeout(() => {
      dispatch(getTopic({ offset: 0, name: value })).then(() => {
        setTimeout(() => {
          inputRef?.current?.focus();
        }, 1000);
      });
    }, 1000);
    setOffsetChangedByUser(false);
    setOffset(0);
    setCurrentPage(1);
    setTimeoutId(newTimeoutId);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (offsetChangedByUser) {
        setIsLoading(true);
        await dispatch(getTopic({ offset, name: filterBy }));
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dispatch, offset]);

  useEffect(() => {
    setTotalPages(Math.ceil(totalResult / 20));
  }, [totalResult]);

  useEffect(() => {
    return () => {
      dispatch(clearTopicData());
      dispatch(clearResources());
    };
  }, []);

  const [isAgenda, setIsAgenda] = useState(false);
  const [isEditTopic, setIsEditTopic] = useState(false);
  const [isTableInputModalOpen, setIsTableInputModalOpen] = useState(false);
  const [textModalOpen, setTextModalOpen] = useState(false);
  const [textModalData, setTextModalData] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [getListId, setGetListId] = useState(0);
  const [isDeleteFor, setIsDeleteFor] = useState(null);
  const [openResourceModal, setOpenResourceModal] = useState(false);
  const [openResourceTable, setOpenResourceTable] = useState(false);
  const [resourceDetails, setResourceDetails] = useState(
    initialResourceDetails
  );
  const [isCloneTopic, setIsCloneTopic] = useState(false);

  const handleDeleteModalOpen = (id, deleteFor) => {
    setIsDeleteFor(deleteFor);
    setGetListId(id);
    setDeleteModalOpen(true);
  };
  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };
  const handleDelete = async () => {
    if (isDeleteFor === "Topic") {
      await dispatch(deleteTopic({ getListId, offset, filterBy }));
    } else if (isDeleteFor === "Resource") {
      await dispatch(deleteResource({ getListId, offset, filterBy }));
      setOpenResourceTable(false);
    }
    setDeleteModalOpen(false);
  };

  const handleLockTopic = async (id, locked) => {
    await dispatch(lockTopic({ id, locked, offset, filterBy }));
  };

  const handleExercise = (id) => {
    navigate(`/topic/${id}/questions`);
  };

  const handleAssessmentQuestions = (id) => {
    navigate(`/topic/${id}/assessmentQuestions`);
  };

  const handleSessions = (id) => {
    navigate(`/topic/${id}/sessions`);
  };

  const tableInputModalOpen = (event, data) => {
    setApiError(false);
    setErrors(initialErrorState);
    setIsEditTopic(false);
    setIsTableInputModalOpen(true);
  };
  const tableInputModalClose = () => {
    setApiError(false);
    setIsEditTopic(false);
    setIsCloneTopic(false);
    setErrors(initialErrorState);
    setIsTableInputModalOpen(false);
    setTopicData(initialTopicData);
  };

  const handleTextModalOpen = (event, data, message, isAgenda) => {
    setIsEmptyMsg(message);
    setTextModalOpen(true);
    setTextModalData(`${data}`);
    setIsAgenda(isAgenda);
  };
  const handleTextModalClose = () => {
    setTextModalOpen(false);
  };

  /////////////////////////////////////////////////////////////
  ////////////////// Add Resource Modal ///////////////////////
  const [resourceApiError, setResourceApiError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const handleResourceModalOpen = (id) => {
    setResourceApiError(false);
    setGetListId(id);
    setOpenResourceModal(true);
  };

  const handleEditResourceModalOpen = (data) => {
    setResourceApiError(false);
    setIsEdit(true);
    setGetListId(data?.topic?.id);
    setResourceDetails({
      json: {
        resourceId: data?.id,
        name: data?.name,
        format: data?.format,
        url: data?.format === "URL" ? data?.url : null,
      },
      files: null,
      offset: offset,
      filterBy: filterBy,
    });
    setOpenResourceModal(true);
  };
  const handleResourceModalClose = () => {
    setResourceApiError(false);
    setOpenResourceModal(false);
    setIsEdit(false);
    setResourceDetails(initialResourceDetails);
  };
  const handleResourceDataChange = (name, value) => {
    setResourceDetails((previousDetails) => {
      if (name.startsWith("json.")) {
        const nestedProperty = name.split(".")[1];
        return {
          ...previousDetails,
          json: {
            ...previousDetails.json,
            [nestedProperty]: value,
          },
          offset,
          filterBy,
        };
      } else {
        return {
          ...previousDetails,
          [name]: value,
        };
      }
    });
  };
  const handleResourceSubmit = async (submittedData) => {
    let apiValue;
    const isValidUrl = () => {
      return (
        !submittedData?.json?.url || isValidUrlFormat(submittedData?.json?.url)
      );
    };

    if (isValidUrl()) {
      submittedData.json.id = getListId;
      if (!isEdit) {
        apiValue = await dispatch(addResource(submittedData));
      } else {
        submittedData.json.id = resourceId;
        apiValue = await dispatch(editResource(submittedData));
      }
    }

    if (apiValue?.error?.message === "Rejected") {
      setResourceApiError(true);
    } else {
      handleResourceModalClose();
      setResourceDetails(initialResourceDetails);
    }
  };
  //////////////////////////////////////////////////////////////////

  const checkLimitedSpecialChar = (value) =>
    /[!@#$%^*+=[\]{};'"\\<>?~]/.test(value);
  const handleResourceTableOpen = (e, topicId, count) => {
    dispatch(getResourceByTopicId(topicId));
    setOpenResourceTable(true);
    setResourceId(topicId);
  };
  const handleResourceTableClose = () => {
    setOpenResourceTable(false);
  };

  const handleTopicDataChange = (name, value) => {
    setApiError(false);
    setErrors((prev) => {
      return {
        ...prev,
        [name]: false,
      };
    });
    setTopicData((previousDetails) => ({
      ...previousDetails,
      [name]: value,
    }));
  };

  const handleSubmitTopic = async (updatedTopicData) => {
    let apiValue;
    if (
      validateFields(updatedTopicData) &&
      !checkLimitedSpecialChar(updatedTopicData?.name)
    ) {
      const data = {
        ...updatedTopicData,
        name: updatedTopicData?.name?.trim(),
      };

      // Include clone fields when cloning
      if (isCloneTopic) {
        data.cloneAssessmentQuestions =
          topicData?.cloneAssessmentQuestions ?? true;
        data.cloneExerciseQuestions = topicData?.cloneExerciseQuestions ?? true;
        data.cloneResources = topicData?.cloneResources ?? true;
        apiValue = await dispatch(cloneTopic(data));
      } else if (isEditTopic) {
        apiValue = await dispatch(editTopic(data));
      } else {
        apiValue = await dispatch(addTopic(data));
      }
      if (apiValue?.error?.message === "Rejected") {
        setApiError(true);
      } else {
        tableInputModalClose();
      }
    }
  };

  const handleEditInputModalOpen = (id, name, description, agenda) => {
    setIsTableInputModalOpen(true);
    setIsEditTopic(true);
    setTopicData({
      id: id,
      name: name,
      description: description,
      agenda: agenda,
      offset,
      filterBy: filterBy,
    });
  };

  //////////////////// validate session /////////////////////////
  const initialErrorState = {
    name: "",
    description: "",
    agenda: "",
  };
  const [errors, setErrors] = useState(initialErrorState);
  const [apiError, setApiError] = useState(false);

  const validateFields = (topicDetails) => {
    const { name, description, agenda } = topicDetails;
    let trimmedName = name.trim();
    const newErrors = {
      name: trimmedName ? "" : "Name is required",
      description: description ? "" : "Description is required",
      agenda: agenda && agenda !== "<p><br></p>" ? "" : "Agenda is required",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };
  //////////////////// Clone Topic /////////////////////////

  const handleCloneModalOpen = (id, name, description, agenda) => {
    setIsTableInputModalOpen(true);
    setIsCloneTopic(true);
    setTopicData({
      id: id,
      name: `Clone Of ${name}`,
      description: description,
      agenda: agenda,
      offset,
      filterBy: filterBy,
      cloneAssessmentQuestions: true,
      cloneExerciseQuestions: true,
      cloneResources: true,
    });
  };

  ///////////////////////////////////////////////////////////////////

  if (loading && isLoading) return <Loader />;

  return (
    <>
      {getTopicData?.loading ? (
        <Box sx={{ display: "flex" }}>
          <Loader />
        </Box>
      ) : (
        <Grid container className="main-container">
          <Grid item className="header-container" md={12}>
            <Typography variant="font_21_bold">Topics</Typography>
            <Box className="header-right-icons">
              <TextField
                size="small"
                placeholder="Search"
                color="secondary"
                value={filterBy}
                onChange={(e) => handleFilterChange(e?.target?.value)}
                className="margin-right-1 topic-search-input"
                type="search"
                inputRef={inputRef}
              />
              {accessToCreate && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={tableInputModalOpen}
                  sx={{ height: "1.875rem" }}
                >
                  <Typography color="secondary" variant="outlineBtnLabel">
                    Create Topic
                  </Typography>
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item md={12} className="body-container">
            <Grid container className="table-parent-container">
              <Paper
                elevation={0}
                className="flex-container-column topic-paper-container"
              >
                {!getTopicData?.data?.results?.length ? (
                  <Grid container className="no-data-container">
                    <NoDataAvailable
                      imgHeight={4}
                      imgWidth={4.5}
                      message="No Topics Available"
                    />
                  </Grid>
                ) : (
                  <TableWithPagination
                    totalResult={totalResult}
                    handleDeleteModalOpen={handleDeleteModalOpen}
                    topicData={getTopicData}
                    handleTextModalOpen={handleTextModalOpen}
                    handleExercise={handleExercise}
                    handleAssessmentQuestions={handleAssessmentQuestions}
                    handleSessions={handleSessions}
                    handleEditInputModalOpen={handleEditInputModalOpen}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                    handleResourceModalOpen={handleResourceModalOpen}
                    handleResourceTableOpen={handleResourceTableOpen}
                    currentPage={currentPage}
                    handleLockTopic={handleLockTopic}
                    handleCloneTopic={handleCloneModalOpen}
                  />
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      )}

      <TopicInputModal
        open={isTableInputModalOpen}
        topicDetails={topicData}
        handleClose={tableInputModalClose}
        handleTopicDataChange={handleTopicDataChange}
        handleSubmitTopic={handleSubmitTopic}
        isEditTopic={isEditTopic}
        errors={errors}
        apiError={apiError}
        checkLimitedSpecialChar={checkLimitedSpecialChar}
        isCloneTopic={isCloneTopic}
      />

      <SimpleModal
        message={isEmptyMsg}
        open={textModalOpen}
        handleClose={handleTextModalClose}
        data={textModalData}
        isAgenda={isAgenda}
      />

      <DeleteModal
        open={deleteModalOpen}
        handleClose={handleDeleteModalClose}
        handleDelete={handleDelete}
      />
      <AddResourceModal
        open={openResourceModal}
        isEdit={isEdit}
        onClose={handleResourceModalClose}
        value={resourceDetails}
        handleChange={handleResourceDataChange}
        handleResourceSubmit={handleResourceSubmit}
        resourceApiError={resourceApiError}
      />

      <ResourceTableModal
        open={openResourceTable}
        handleClose={handleResourceTableClose}
        tableData={topic?.resources?.data}
        handleDeleteModalOpen={handleDeleteModalOpen}
        isResourceLoading={isResourceLoading}
        viaTopic={true}
        handleEditModalOpen={handleEditResourceModalOpen}
      />
    </>
  );
};

export default Topic;
